// @ts-check
import { gql } from '@apollo/client';
import { Document } from '@react-pdf/renderer';
import React from 'react';

export default function asInfoSheet(InfoSheet) {
  if (!InfoSheet.useMetaData) {
    InfoSheet.useMetaData = (product) => ({
      title: `${product?.name} Info Sheet`,
    });
  }
  return {
    beforeQuery,
    query: getQuery(InfoSheet.fragment),
    layout: (props) => <Layout Component={InfoSheet} {...props} />,
  };
}

function Layout({ data, Component, useMetaData, ...rest }) {
  const { product, ...dataRest } = afterQuery(data);
  const metadata = Component.useMetaData(product);
  return (
    <Document title={metadata?.title}>
      <Component
        key={product?.productId}
        data={product}
        {...dataRest}
        {...rest}
      />
    </Document>
  );
}

/**
 * Creates the query variables.
 * @param {object} props
 * @param {string} props.workspaceId The workspaceId.
 * @param {string} props.productId The product to view.
 * @param {import('@bottlebooks/graphql-server/src').ContentLocale} [props.locale] The locale - defaults to en.
 * @returns {import('@apollo/client').QueryHookOptions<any, { workspaceId?: string; productId: string, locale: import('@bottlebooks/graphql-server/src').ContentLocale}>}
 */

function beforeQuery({ workspaceId, productId, locale = 'en' }) {
  return { variables: { workspaceId, productId, locale } };
}

/**
 *
 * Creates a query from a fragment.
 * @param {import('graphql').DocumentNode} fragment
 */
function getQuery(fragment) {
  const firstDefinition = fragment.definitions[0];
  if (firstDefinition?.kind !== 'FragmentDefinition')
    throw new Error('You need to pass a fragment!');
  if (firstDefinition.typeCondition.name.value !== 'Product')
    throw new Error('You must pass a fragment on Product');
  const parse = gql; // Renamed to avoid IDE errors
  return parse/* GraphQL */ `
    query InfoSheet_${firstDefinition.name.value}(
      $workspaceId: ID!
      $productId: ID!
      $locale: ContentLocale
    ) {
      workspace(workspaceId: $workspaceId, locale: $locale) {
        product(productId: $productId) {
          productId
          name
          ...${firstDefinition.name.value}
        }
      }
    }

    ${fragment}
  `;
}

function afterQuery(data) {
  return { ...data, product: data?.workspace?.product };
}
